'use client';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { AccessoryProductVariant } from '@eliadmin/core/src/api/products/types/product-types';
import { OfferGroup } from '@eliadmin/core/src/components/dlz/product-detail/extras/offer-parts';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type MobileView = 'text' | 'image';
const MOBILE_VISIBLE_COUNT = 6;

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2';
const TILE = 'rounded-[18px] border bg-white transition-colors';
const TILE_TONE = {
  current: 'border-[#0F1012] shadow-[inset_0_0_0_1px_#0F1012]',
  link: 'border-[#E2E4E7] hover:border-[#9096A0]',
} as const;

function TextViewIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <path d="M3 4.5h10" />
      <path d="M3 8h7.5" />
      <path d="M3 11.5h9" />
    </svg>
  );
}

function ImageViewIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="size-4" fill="currentColor">
      <rect x="2" y="2" width="5" height="5" rx="1" />
      <rect x="9" y="2" width="5" height="5" rx="1" />
      <rect x="2" y="9" width="5" height="5" rx="1" />
      <rect x="9" y="9" width="5" height="5" rx="1" />
    </svg>
  );
}

/**
 * Sibling products that differ in an accessory-like property („Varianty“): the product itself plus
 * `accessory_product_variants`, cheapest first, each a link to its own detail page. Image tiles
 * from `md`; on phones a text list or an image grid, shortened to six entries.
 */
export const ProductAccessoriesVariants = () => {
  const product = useVariantsStore(s => s.product)!;
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const priceWithoutVatFirst = useGlobalStore(
    s => s.templateSettings.product_detail?.price_without_vat_first?.enabled,
  );
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const router = useRouter();
  const [mobileView, setMobileView] = useState<MobileView>('text');
  const [showAllMobile, setShowAllMobile] = useState(false);
  const { t } = useTranslations();

  const accessoryVariants = useMemo(() => product.accessory_product_variants ?? [], [product]);

  const currentProductId = product.id;
  const sortedVariants = useMemo(() => {
    const currentProductAsVariant = product as AccessoryProductVariant;

    return [currentProductAsVariant, ...accessoryVariants].sort((a, b) => {
      const aPriceDisplay = getProductPriceDisplay(a, { isWholesaler, priceWithoutVatFirst });
      const bPriceDisplay = getProductPriceDisplay(b, { isWholesaler, priceWithoutVatFirst });
      const aPrice = aPriceDisplay.priceOnRequest
        ? Number.POSITIVE_INFINITY
        : (aPriceDisplay.current ?? Number.POSITIVE_INFINITY);
      const bPrice = bPriceDisplay.priceOnRequest
        ? Number.POSITIVE_INFINITY
        : (bPriceDisplay.current ?? Number.POSITIVE_INFINITY);
      if (aPrice !== bPrice) return aPrice - bPrice;
      return a.id - b.id;
    });
  }, [accessoryVariants, isWholesaler, priceWithoutVatFirst, product]);

  const formatVariantPrice = (variant: AccessoryProductVariant) => {
    const priceDisplay = getProductPriceDisplay(variant, {
      isWholesaler,
      priceWithoutVatFirst,
    });

    return priceDisplay.priceOnRequest
      ? t('text2278')
      : formatPrice(priceDisplay.current, currency, currencySettings);
  };

  useEffect(() => {
    for (const variant of sortedVariants) {
      if (variant.id === currentProductId || !variant.slug) continue;

      try {
        router.prefetch(`/${variant.slug}`);
      } catch {
        // Prefetch is opportunistic and should never affect variant rendering.
      }
    }
  }, [currentProductId, router, sortedVariants]);

  if (accessoryVariants.length === 0) {
    return null;
  }

  const visibleMobileVariants = showAllMobile
    ? sortedVariants
    : sortedVariants.slice(0, MOBILE_VISIBLE_COUNT);
  const hiddenMobileCount = sortedVariants.length - MOBILE_VISIBLE_COUNT;

  /** The current product is a plain element, every other variant a link to its page. */
  const variantItem = (variant: AccessoryProductVariant, className: string, content: ReactNode) =>
    variant.id === currentProductId ? (
      <div key={variant.id} aria-current="true" className={className}>
        {content}
      </div>
    ) : (
      <Link
        key={variant.id}
        href={`/${variant.slug}`}
        prefetch={true}
        replace
        className={cn(className, FOCUS)}
      >
        {content}
      </Link>
    );

  const tone = (variant: AccessoryProductVariant) =>
    variant.id === currentProductId ? TILE_TONE.current : TILE_TONE.link;

  const variantImage = (variant: AccessoryProductVariant, className: string) => (
    <span className={cn('relative block shrink-0 overflow-hidden rounded-[12px]', className)}>
      <Image
        src={variant.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(variant.image, variant.name)}
        fill
        sizes={buildImageSizes([{ size: '64px' }])}
        className="object-contain object-center"
      />
    </span>
  );

  return (
    <OfferGroup title={t('text826')} className="ea-product-accessories-variants">
      <div
        role="group"
        aria-label={t('text826')}
        className="ea-product-accessories-variants__list hidden flex-wrap gap-2 md:flex"
      >
        {sortedVariants.map(variant => (
          <div key={variant.id} className="group relative">
            {variantItem(
              variant,
              cn('flex w-[92px] flex-col items-center gap-1 p-2', TILE, tone(variant)),
              <>
                {variantImage(variant, 'size-14')}
                <span className="dlz-caption font-semibold text-[#0F1012]">
                  {formatVariantPrice(variant)}
                </span>
                <span className="sr-only">{variant.name}</span>
              </>,
            )}
            <span
              aria-hidden="true"
              className="dlz-caption pointer-events-none absolute -top-2 left-1/2 z-50 w-max max-w-[220px] -translate-x-1/2 -translate-y-full rounded-[6px] bg-[#0F1012] px-2 py-1 text-center text-white opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none"
            >
              {variant.name}
            </span>
          </div>
        ))}
      </div>

      <div className="flex justify-end md:hidden">
        <div className="inline-flex rounded-full border border-[#E2E4E7] bg-white p-0.5">
          {(
            [
              ['text', 'Text', TextViewIcon],
              ['image', 'Image', ImageViewIcon],
            ] as const
          ).map(([view, label, ViewIcon]) => (
            <button
              key={view}
              type="button"
              aria-label={label}
              aria-pressed={mobileView === view}
              onClick={() => {
                setMobileView(view);
                setShowAllMobile(false);
              }}
              className={cn(
                'flex size-8 items-center justify-center rounded-full transition-colors',
                FOCUS,
                mobileView === view ? 'bg-[#0F1012] text-white' : 'text-[#6B7079]',
              )}
            >
              <ViewIcon />
            </button>
          ))}
        </div>
      </div>

      {mobileView === 'text' ? (
        <div className="flex flex-col gap-2 md:hidden">
          {visibleMobileVariants.map(variant =>
            variantItem(
              variant,
              cn('flex w-full items-center justify-between gap-3 px-4 py-3', TILE, tone(variant)),
              <>
                <span className="dlz-body-s min-w-0 flex-1 text-[#0F1012]">{variant.name}</span>
                <span className="dlz-body-s-medium shrink-0 text-[#D7191F]">
                  {formatVariantPrice(variant)}
                </span>
              </>,
            ),
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 md:hidden">
          {visibleMobileVariants.map(variant =>
            variantItem(
              variant,
              cn('flex min-w-0 flex-col items-center gap-1 p-2 text-center', TILE, tone(variant)),
              <>
                {variantImage(variant, 'size-14')}
                <span className="dlz-caption line-clamp-3 text-[#0F1012]">{variant.name}</span>
                <span className="dlz-caption font-semibold text-[#D7191F]">
                  {formatVariantPrice(variant)}
                </span>
              </>,
            ),
          )}
        </div>
      )}

      {hiddenMobileCount > 0 ? (
        <button
          type="button"
          aria-expanded={showAllMobile}
          onClick={() => setShowAllMobile(prev => !prev)}
          className={cn(
            'dlz-body-s-medium self-start rounded-sm text-[#D7191F] hover:underline md:hidden',
            FOCUS,
          )}
        >
          {showAllMobile
            ? t('text604')
            : `${t('text3359').charAt(0).toUpperCase()}${t('text3359').slice(1)} (${hiddenMobileCount})`}
        </button>
      ) : null}
    </OfferGroup>
  );
};
